/**
 * Scene-scoped object pool for the combat hot path.
 *
 * Why this exists: damage numbers, gold popups, arrow trails and death
 * particles used to allocate a fresh GameObject *and* a Tween per event. A
 * busy wave at 4x speed produced several hundred allocations per second,
 * which showed up as GC hitches. This pool reuses a bounded set of objects and
 * integrates their motion manually in update(), so steady-state combat
 * allocates no GameObjects and no Tweens — those were the expensive ones, each
 * carrying a canvas texture upload or a tween-manager registration. (Each
 * spawn still builds one small Motion record; that is deliberate, since the
 * alternative is an unreadable positional-argument API for no real gain.)
 *
 * Second benefit: update() is driven by GameScene's speed-scaled delta, so
 * effects now play back at game speed instead of lagging behind at 2x/4x.
 *
 * Lifecycle: GameScene calls fx.attach(this) in create() and fx.update(d)
 * from its update loop. Every spawn method is a no-op before attach, so
 * importing the singleton at module load is safe.
 */
import Phaser from "phaser";
import { WALK_FRAME_SUFFIXES } from "../config";
import { THEME } from "../ui/theme";

type Ease = (t: number) => number;

const easeOutCubic: Ease = t => 1 - Math.pow(1 - t, 3);
const easeOutQuad: Ease = t => 1 - (1 - t) * (1 - t);
const easeLinear: Ease = t => t;

/** Structural type covering Text / Rectangle / Ellipse / Image. */
type FxObject = Phaser.GameObjects.GameObject & {
  x: number;
  y: number;
  alpha: number;
  scaleX: number;
  scaleY: number;
  setVisible(v: boolean): unknown;
  setDepth(v: number): unknown;
};

interface Motion {
  vx: number;
  vy: number;
  /** Gravity applied to vy, px/s^2. */
  ay: number;
  /** Remaining lifetime in ms. */
  life: number;
  maxLife: number;
  /** X and Y scale are tracked separately so trails can shrink in length only. */
  scaleXFrom: number;
  scaleXTo: number;
  scaleYFrom: number;
  scaleYTo: number;
  alphaFrom: number;
  alphaTo: number;
  ease: Ease;
}

interface Slot<T extends FxObject> {
  obj: T;
  m: Motion;
  active: boolean;
  /**
   * Signature of the text style last applied to this slot.
   *
   * Phaser's TextStyle.setColor() has no change guard — it calls update()
   * unconditionally, which re-rasterizes the text canvas and re-uploads the
   * WebGL texture. Applying the same colour to every damage number therefore
   * paid for a full redraw per hit. Tracking what is already on the slot lets
   * the common case (a stream of identical non-crit numbers) skip it.
   * setFontSize/setStroke self-guard, so they cost only on crit transitions.
   */
  style?: string;
  /**
   * Animation frames for this spawn, in order, or undefined when the texture
   * has none. Played once across the lifetime — an explosion is not a loop.
   */
  frames?: string[];
  frameIdx?: number;
}

/** Hard caps. Once reached a pool recycles round-robin instead of growing. */
const CAP_TEXT = 56;
const CAP_QUAD = 320;
const CAP_BLOB = 64;
const CAP_SPRITE = 128;

class FxPool {
  private scene?: Phaser.Scene;

  private texts: Slot<Phaser.GameObjects.Text>[] = [];
  private quads: Slot<Phaser.GameObjects.Rectangle>[] = [];
  private blobs: Slot<Phaser.GameObjects.Ellipse>[] = [];
  private sprites: Slot<Phaser.GameObjects.Image>[] = [];

  /** Source dimensions per texture key, resolved once. Cleared on attach. */
  private texSize = new Map<string, { w: number; h: number }>();

  /** Animation frames per texture key, resolved once. Cleared on attach. */
  private frameKeys = new Map<string, string[] | undefined>();

  /** Round-robin steal cursors, used once a pool is at capacity. */
  private textCursor = 0;
  private quadCursor = 0;
  private blobCursor = 0;
  private spriteCursor = 0;

  /** Live effect count, exposed for the perf overlay. */
  get activeCount(): number {
    let n = 0;
    for (const s of this.texts) if (s.active) n++;
    for (const s of this.quads) if (s.active) n++;
    for (const s of this.blobs) if (s.active) n++;
    for (const s of this.sprites) if (s.active) n++;
    return n;
  }

  /** Total pooled objects allocated so far, exposed for the perf overlay. */
  get pooledCount(): number {
    return this.texts.length + this.quads.length + this.blobs.length + this.sprites.length;
  }

  /**
   * Bind to a scene. Pools are per-scene: Phaser destroys the GameObjects on
   * shutdown, so we drop the old slots rather than reuse dead references.
   */
  attach(scene: Phaser.Scene): void {
    this.scene = scene;
    this.texts = [];
    this.quads = [];
    this.blobs = [];
    this.sprites = [];
    this.textCursor = 0;
    this.quadCursor = 0;
    this.blobCursor = 0;
    this.spriteCursor = 0;
    // Textures can be regenerated between runs, so don't trust stale sizes.
    this.texSize.clear();
    this.frameKeys.clear();
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.detach(scene));
  }

  /** Drop references so a stale scene can be garbage collected. */
  detach(scene: Phaser.Scene): void {
    if (this.scene !== scene) return;
    this.scene = undefined;
    this.texts = [];
    this.quads = [];
    this.blobs = [];
    this.sprites = [];
  }

  /**
   * Animation frames registered for a texture, or undefined when it has none.
   * Cached: a poison cloud re-spawns constantly and re-probing the texture
   * manager per spawn is pure overhead. Cleared on attach with the size cache.
   */
  private framesFor(key: string): string[] | undefined {
    const cached = this.frameKeys.get(key);
    if (cached !== undefined || this.frameKeys.has(key)) return cached;
    const scene = this.scene;
    const list = [key];
    if (scene) {
      for (const suffix of WALK_FRAME_SUFFIXES.slice(1)) {
        if (!scene.textures.exists(`${key}${suffix}`)) break;
        list.push(`${key}${suffix}`);
      }
    }
    const frames = list.length > 1 ? list : undefined;
    this.frameKeys.set(key, frames);
    return frames;
  }

  /* ---------------------------------------------------------------------- */
  /*                                 Spawners                               */
  /* ---------------------------------------------------------------------- */

  /** Floating damage number. `crit` renders slightly bigger and hotter. */
  damageNumber(x: number, y: number, amount: number, color = "#ffe070", crit = false): void {
    const s = this.acquireText();
    if (!s) return;
    const t = s.obj;
    this.applyTextStyle(s, crit ? "#ff9a3c" : color, crit ? 19 : 16, crit ? 4 : 3);
    t.setText(String(Math.round(amount)));
    t.setPosition(x, y - 8);
    t.setDepth(THEME.depths.overlay);
    const life = crit ? 650 : 600;
    this.launch(s, {
      vx: (Math.random() - 0.5) * 22,
      vy: crit ? -56 : -46,
      ay: 46,
      life, maxLife: life,
      scaleXFrom: crit ? 1.15 : 1, scaleXTo: 1,
      scaleYFrom: crit ? 1.15 : 1, scaleYTo: 1,
      alphaFrom: 1, alphaTo: 0,
      ease: easeOutCubic
    });
  }

  /** Gold reward popup at an enemy death position. */
  goldPopup(x: number, y: number, amount: number): void {
    const s = this.acquireText();
    if (!s) return;
    const t = s.obj;
    this.applyTextStyle(s, "#ffe070", 15, 3);
    t.setText(`+${amount}g`);
    t.setPosition(x, y - 18);
    t.setDepth(THEME.depths.overlay);
    this.launch(s, {
      vx: 0, vy: -34, ay: 8,
      life: 800, maxLife: 800,
      scaleXFrom: 1, scaleXTo: 1,
      scaleYFrom: 1, scaleYTo: 1,
      alphaFrom: 1, alphaTo: 0,
      ease: easeOutCubic
    });
  }

  /** Radial burst of small colored quads — used on enemy death. */
  burst(x: number, y: number, color: number, count = 8, speed = 46): void {
    for (let i = 0; i < count; i++) {
      const s = this.acquireQuad();
      if (!s) return;
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const v = speed * (0.6 + Math.random() * 0.7);
      const q = s.obj;
      q.setPosition(x, y);
      q.setSize(3, 3);
      q.fillColor = color;
      q.setDepth(THEME.depths.boss);
      this.launch(s, {
        vx: Math.cos(angle) * v, vy: Math.sin(angle) * v, ay: 60,
        life: 450, maxLife: 450,
        scaleXFrom: 1, scaleXTo: 0.5,
        scaleYFrom: 1, scaleYTo: 0.5,
        alphaFrom: 1, alphaTo: 0,
        ease: easeOutCubic
      });
    }
  }

  /** Short outward spark streaks at an impact point. */
  sparks(x: number, y: number, count = 3, color = 0xffd070): void {
    for (let i = 0; i < count; i++) {
      const s = this.acquireQuad();
      if (!s) return;
      const a = Math.random() * Math.PI * 2;
      const v = 48 + Math.random() * 38;
      const q = s.obj;
      q.setPosition(x, y);
      q.setSize(3, 3);
      q.fillColor = color;
      q.setDepth(THEME.depths.boss + 50);
      this.launch(s, {
        vx: Math.cos(a) * v, vy: Math.sin(a) * v, ay: 0,
        life: 220, maxLife: 220,
        scaleXFrom: 1, scaleXTo: 0.4,
        scaleYFrom: 1, scaleYTo: 0.4,
        alphaFrom: 0.95, alphaTo: 0,
        ease: easeOutCubic
      });
    }
  }

  /** Rising smoke/dust puff. Larger, slower and softer than sparks. */
  puff(x: number, y: number, color: number, radius = 5, alpha = 0.55): void {
    const s = this.acquireBlob();
    if (!s) return;
    const b = s.obj;
    b.setPosition(x, y);
    b.setSize(radius * 2, radius * 2);
    b.fillColor = color;
    b.setDepth(THEME.depths.hud);
    this.launch(s, {
      vx: (Math.random() - 0.5) * 14, vy: -18 - Math.random() * 14, ay: 0,
      life: 700, maxLife: 700,
      scaleXFrom: 0.7, scaleXTo: 1.8,
      scaleYFrom: 0.7, scaleYTo: 1.8,
      alphaFrom: alpha, alphaTo: 0,
      ease: easeOutQuad
    });
  }

  /** Dark ground splat that lingers then fades. */
  decal(x: number, y: number, w = 14, h = 5, color = 0x1a0a04, alpha = 0.5): void {
    const s = this.acquireBlob();
    if (!s) return;
    const b = s.obj;
    b.setPosition(x, y);
    b.setSize(w, h);
    b.fillColor = color;
    b.setDepth(50);
    this.launch(s, {
      vx: 0, vy: 0, ay: 0,
      life: 1200, maxLife: 1200,
      scaleXFrom: 1, scaleXTo: 1,
      scaleYFrom: 1, scaleYTo: 1,
      alphaFrom: alpha, alphaTo: 0,
      ease: easeLinear
    });
  }

  /**
   * Pooled textured effect sprite — trails, impact bursts, atmosphere motes.
   * `w`/`h` are the on-screen size at scale 1; `growTo` scales that up or down
   * over the lifetime. No-op when the texture doesn't exist, matching the
   * fallback behaviour effects.ts relies on.
   */
  sprite(key: string, x: number, y: number, o: {
    /** On-screen width at scale 1. Omit to render at the texture's native size. */
    w?: number;
    h?: number;
    lifeMs: number;
    rotation?: number;
    depth?: number;
    alphaFrom?: number;
    alphaTo?: number;
    /** Multiplier applied to the starting size at spawn. Default 1. */
    startScale?: number;
    /** Multiplier applied to the starting size at death. Default 1. */
    growTo?: number;
    /** Independent X multiplier at death — lets trails shrink in length only. */
    growToX?: number;
    vx?: number;
    vy?: number;
    additive?: boolean;
    ease?: Ease;
  }): void {
    const scene = this.scene;
    if (!scene || !scene.textures.exists(key)) return;
    const s = this.acquireSprite();
    if (!s) return;
    const img = s.obj;
    s.frames = this.framesFor(key);
    s.frameIdx = 0;
    img.setTexture(key);
    img.setPosition(x, y);
    img.setRotation(o.rotation ?? 0);
    img.setDepth(o.depth ?? THEME.depths.effects);
    img.setBlendMode(o.additive ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);

    // Convert the requested pixel size into scale factors against the source
    // texture, so the pool can drive scaleX/scaleY directly. The source lookup
    // is cached: arrow trails re-spawn every 45ms per arrow in flight, and
    // re-resolving the texture each time is pure overhead.
    let src = this.texSize.get(key);
    if (!src) {
      const img = scene.textures.get(key).getSourceImage();
      src = { w: img.width, h: img.height };
      this.texSize.set(key, src);
    }
    const baseX = o.w != null && src.w ? o.w / src.w : 1;
    const baseY = o.h != null && src.h ? o.h / src.h : 1;
    const start = o.startScale ?? 1;
    const endX = o.growToX ?? o.growTo ?? 1;
    const endY = o.growTo ?? 1;

    this.launch(s, {
      vx: o.vx ?? 0, vy: o.vy ?? 0, ay: 0,
      life: o.lifeMs, maxLife: o.lifeMs,
      scaleXFrom: baseX * start, scaleXTo: baseX * endX,
      scaleYFrom: baseY * start, scaleYTo: baseY * endY,
      alphaFrom: o.alphaFrom ?? 1, alphaTo: o.alphaTo ?? 0,
      ease: o.ease ?? easeOutCubic
    });
  }

  /* ---------------------------------------------------------------------- */
  /*                                  Update                                */
  /* ---------------------------------------------------------------------- */

  /** Integrate every live effect. `deltaMs` should be the speed-scaled delta. */
  update(deltaMs: number): void {
    if (!this.scene) return;
    const dt = deltaMs / 1000;
    this.step(this.texts, deltaMs, dt);
    this.step(this.quads, deltaMs, dt);
    this.step(this.blobs, deltaMs, dt);
    this.step(this.sprites, deltaMs, dt);
  }

  private step<T extends FxObject>(pool: Slot<T>[], deltaMs: number, dt: number): void {
    for (const s of pool) {
      if (!s.active) continue;
      const m = s.m;
      m.life -= deltaMs;
      if (m.life <= 0) {
        this.release(s);
        continue;
      }
      m.vy += m.ay * dt;
      s.obj.x += m.vx * dt;
      s.obj.y += m.vy * dt;
      // Frames run on raw lifetime, scale and alpha on the eased curve: an
      // eased frame track would hold the first image and then flick through the
      // rest, which reads as a stutter rather than an animation.
      const t = 1 - m.life / m.maxLife;
      const k = m.ease(t);
      s.obj.alpha = m.alphaFrom + (m.alphaTo - m.alphaFrom) * k;
      s.obj.scaleX = m.scaleXFrom + (m.scaleXTo - m.scaleXFrom) * k;
      s.obj.scaleY = m.scaleYFrom + (m.scaleYTo - m.scaleYFrom) * k;
      if (s.frames) {
        const idx = Math.min(s.frames.length - 1, Math.floor(t * s.frames.length));
        if (idx !== s.frameIdx) {
          s.frameIdx = idx;
          (s.obj as unknown as Phaser.GameObjects.Image).setTexture(s.frames[idx]);
        }
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /*                               Pool plumbing                            */
  /* ---------------------------------------------------------------------- */

  /**
   * Apply colour/size/stroke only when they differ from what the slot already
   * carries. See Slot.style for why this matters.
   */
  private applyTextStyle(
    s: Slot<Phaser.GameObjects.Text>, color: string, size: number, stroke: number
  ): void {
    const sig = `${color}|${size}|${stroke}`;
    if (s.style === sig) return;
    s.obj.setColor(color);
    s.obj.setFontSize(size);
    s.obj.setStroke("#000000", stroke);
    s.style = sig;
  }

  private launch<T extends FxObject>(s: Slot<T>, m: Motion): void {
    s.m = m;
    s.active = true;
    s.obj.alpha = m.alphaFrom;
    s.obj.scaleX = m.scaleXFrom;
    s.obj.scaleY = m.scaleYFrom;
    s.obj.setVisible(true);
  }

  private release<T extends FxObject>(s: Slot<T>): void {
    s.active = false;
    s.obj.setVisible(false);
  }

  private acquireText(): Slot<Phaser.GameObjects.Text> | null {
    const scene = this.scene;
    if (!scene) return null;
    const free = this.texts.find(s => !s.active);
    if (free) return free;
    if (this.texts.length < CAP_TEXT) {
      const obj = scene.add.text(0, 0, "", {
        fontFamily: THEME.fonts.body,
        fontSize: "16px",
        color: "#ffe070",
        stroke: "#000000",
        strokeThickness: 3
      }).setOrigin(0.5).setVisible(false);
      const slot: Slot<Phaser.GameObjects.Text> = { obj, m: DEAD, active: false };
      this.texts.push(slot);
      return slot;
    }
    // At capacity: steal round-robin so the oldest effect is the one cut short.
    const slot = this.texts[this.textCursor];
    this.textCursor = (this.textCursor + 1) % this.texts.length;
    return slot;
  }

  private acquireQuad(): Slot<Phaser.GameObjects.Rectangle> | null {
    const scene = this.scene;
    if (!scene) return null;
    const free = this.quads.find(s => !s.active);
    if (free) return free;
    if (this.quads.length < CAP_QUAD) {
      const obj = scene.add.rectangle(0, 0, 3, 3, 0xffffff).setVisible(false);
      const slot: Slot<Phaser.GameObjects.Rectangle> = { obj, m: DEAD, active: false };
      this.quads.push(slot);
      return slot;
    }
    const slot = this.quads[this.quadCursor];
    this.quadCursor = (this.quadCursor + 1) % this.quads.length;
    return slot;
  }

  private acquireBlob(): Slot<Phaser.GameObjects.Ellipse> | null {
    const scene = this.scene;
    if (!scene) return null;
    const free = this.blobs.find(s => !s.active);
    if (free) return free;
    if (this.blobs.length < CAP_BLOB) {
      const obj = scene.add.ellipse(0, 0, 8, 8, 0xffffff).setVisible(false);
      const slot: Slot<Phaser.GameObjects.Ellipse> = { obj, m: DEAD, active: false };
      this.blobs.push(slot);
      return slot;
    }
    const slot = this.blobs[this.blobCursor];
    this.blobCursor = (this.blobCursor + 1) % this.blobs.length;
    return slot;
  }

  private acquireSprite(): Slot<Phaser.GameObjects.Image> | null {
    const scene = this.scene;
    if (!scene) return null;
    const free = this.sprites.find(s => !s.active);
    if (free) return free;
    if (this.sprites.length < CAP_SPRITE) {
      const obj = scene.add.image(0, 0, "__DEFAULT").setVisible(false);
      const slot: Slot<Phaser.GameObjects.Image> = { obj, m: DEAD, active: false };
      this.sprites.push(slot);
      return slot;
    }
    const slot = this.sprites[this.spriteCursor];
    this.spriteCursor = (this.spriteCursor + 1) % this.sprites.length;
    return slot;
  }
}

/** Shared inert motion used as the initial value for a fresh slot. */
const DEAD: Motion = {
  vx: 0, vy: 0, ay: 0, life: 0, maxLife: 1,
  scaleXFrom: 1, scaleXTo: 1, scaleYFrom: 1, scaleYTo: 1,
  alphaFrom: 0, alphaTo: 0, ease: easeLinear
};

export const fx = new FxPool();
